/**
 * Moderación automática del foro (transcrito de la 1.0 functions/lib/profanity.js): normaliza
 * (minúsculas, sin acentos, leet-speak básico, repeticiones colapsadas) y busca términos prohibidos
 * con límites de palabra. No pretende ser exhaustivo: cubre los insultos más comunes de los 14
 * idiomas de la 1.0; los 19 restantes de la 2.0 se irán añadiendo con lo que vea la moderación.
 */
const BANNED: readonly string[] = [
  // --- Inglés ---
  'fuck',
  'fucker',
  'fucking',
  'motherfucker',
  'shit',
  'bullshit',
  'bitch',
  'cunt',
  'asshole',
  'dickhead',
  'bastard',
  'slut',
  'whore',
  'nigger',
  'nigga',
  'faggot',
  'retard',
  'wanker',
  'twat',
  'pussy',
  'cock',
  // --- Español ---
  'puta',
  'puto',
  'putada',
  'mierda',
  'gilipollas',
  'cabron',
  'cabrona',
  'coño',
  'joder',
  'polla',
  'pendejo',
  'maricon',
  'zorra',
  'hijoputa',
  'hijodeputa',
  'follar',
  'capullo',
  'subnormal',
  'mongolo',
  'retrasado',
  // --- Francés ---
  'putain',
  'merde',
  'connard',
  'connasse',
  'salope',
  'enculé',
  'encule',
  'pute',
  'batard',
  'bordel',
  'nique',
  'niquer',
  'pd',
  'pédé',
  'pede',
  'con',
  // --- Alemán ---
  'scheisse',
  'scheiße',
  'arschloch',
  'fotze',
  'hurensohn',
  'schlampe',
  'wichser',
  'fick',
  'ficken',
  'hure',
  'nutte',
  'schwuchtel',
  // --- Italiano ---
  'cazzo',
  'merda',
  'stronzo',
  'stronza',
  'puttana',
  'troia',
  'vaffanculo',
  'coglione',
  'bastardo',
  'figa',
  'stronzata',
  // --- Portugués ---
  'merda',
  'caralho',
  'foda',
  'foder',
  'puta',
  'cabrão',
  'cabrao',
  'cona',
  'filho da puta',
  'buceta',
  'viado',
  'corno',
  // --- Neerlandés ---
  'klootzak',
  'kut',
  'lul',
  'hoer',
  'kanker',
  'godverdomme',
  'tyfus',
  'slet',
  // --- Polaco ---
  'kurwa',
  'chuj',
  'huj',
  'jebac',
  'jebać',
  'pierdol',
  'pierdolic',
  'skurwysyn',
  'kutas',
  'cipa',
  'suka',
  'dziwka',
  'pedal',
  'pedał',
  // --- Sueco ---
  'fitta',
  'kuk',
  'hora',
  'jävla',
  'javla',
  'skit',
  'fan',
  'kärring',
  'karring',
  // --- Danés ---
  'lort',
  'kraft edeme',
  'luder',
  'pikansjos',
  'svin',
  'røvhul',
  'rovhul',
  // --- Finés ---
  'vittu',
  'perkele',
  'saatana',
  'paska',
  'huora',
  'kyrpä',
  'kyrpa',
  'mulkku',
  // --- Noruego ---
  'faen',
  'dritt',
  'helvete',
  'hore',
  'fitte',
  'kuk',
  'rævhøl',
  'ravhol',
  // --- Rumano ---
  'pula',
  'pizda',
  'muie',
  'futu',
  'cacat',
  'căcat',
  'curva',
  'dracu',
  'coaie',
  // --- Húngaro ---
  'bazmeg',
  'baszd',
  'baszni',
  'kurva',
  'geci',
  'fasz',
  'picsa',
  'szar',
  'köcsög',
  'kocsog',
  'buzi',
  'ribanc',
];

const LEET: Record<string, string> = {
  '0': 'o',
  '1': 'i',
  '3': 'e',
  '4': 'a',
  '5': 's',
  '7': 't',
  '8': 'b',
  '@': 'a',
  $: 's',
};

const stripAccents = (s: string): string => s.normalize('NFD').replace(/[̀-ͯ]/g, '');

/** Minúsculas, sin acentos, leet-letras y 3+ repeticiones colapsadas a 2. */
export function normalizeProfanity(text: string): string {
  let s = stripAccents(text.toLowerCase());
  s = s.replace(/[0134578@$]/g, (c) => LEET[c] ?? c);
  s = s.replace(/(.)\1{2,}/g, '$1$1');
  return s;
}

const PATTERNS = BANNED.map((w) => {
  const norm = normalizeProfanity(w).replace(/\s+/g, '\\s*');
  const escaped = norm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return { word: w, re: new RegExp(`(^|[^\\p{L}])${escaped}([^\\p{L}]|$)`, 'iu') };
});

/** Términos prohibidos detectados (vacío = limpio). */
export function detectProfanity(text: string): string[] {
  if (!text) return [];
  const norm = normalizeProfanity(text);
  const collapsed = norm.replace(/(.)\1+/g, '$1');
  return PATTERNS.filter(({ re }) => re.test(norm) || re.test(collapsed)).map((p) => p.word);
}

export const containsProfanity = (text: string): boolean => detectProfanity(text).length > 0;
